import { createContext, useCallback, useEffect, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useBoolean from "hooks/useBoolean";

export interface SidebarType {
  open: boolean;
  alwaysClosed: boolean;
}

export const SidebarContext = createContext<SidebarType | undefined>(undefined);
SidebarContext.displayName = "SidebarContext";

export interface SidebarActionsType {
  toggleSidebar: () => void;
  onOpen: () => void;
  onClose: () => void;
}

export const SidebarActionsContext = createContext<SidebarActionsType | undefined>(undefined);
SidebarActionsContext.displayName = "SidebarActionsContext";

interface SidebarProviderProps {
  children?: React.ReactNode;
}

function SidebarProvider(props: SidebarProviderProps) {
  const theme = useTheme();
  const alwaysClosed = useMediaQuery(theme.breakpoints.down(1290));

  const {
    value: open,
    setValue,
    setFalse,
  } = useBoolean(LocalStorageHelper.get(LocalStorageHelper.keys.drawerMenuOpen, false));

  const toggleSidebar = useCallback(() => {
    setValue((open) => {
      LocalStorageHelper.set(LocalStorageHelper.keys.drawerMenuOpen, !open);
      return !open;
    });
  }, [setValue]);

  const onOpen = useCallback(() => {
    setValue(() => {
      LocalStorageHelper.set(LocalStorageHelper.keys.drawerMenuOpen, true);
      return true;
    });
  }, [setValue]);

  const onClose = useCallback(() => {
    setValue(() => {
      LocalStorageHelper.set(LocalStorageHelper.keys.drawerMenuOpen, false);
      return false;
    });
  }, [setValue]);

  useEffect(() => {
    if (alwaysClosed) setFalse();
  }, [alwaysClosed, setFalse]);

  const value = useMemo(() => ({ open, alwaysClosed }), [alwaysClosed, open]);
  const actions = useMemo(() => ({ toggleSidebar, onOpen, onClose }), [onClose, onOpen, toggleSidebar]);

  return (
    <SidebarContext.Provider value={value}>
      <SidebarActionsContext.Provider value={actions}>{props.children}</SidebarActionsContext.Provider>
    </SidebarContext.Provider>
  );
}

export default SidebarProvider;
